import { memo, useCallback, useContext, useMemo, useState, type MouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, type IntlShape } from 'react-intl'
import type { TripChatUiComponentData } from '@luxuryescapes/contract-trip'
import clsx from 'clsx'

import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Image from 'components/Common/Image'
import GeoContext from 'contexts/geoContext'
import { getHotelRoomOptions } from 'tripPlanner/api'
import type {
  HotelRoomOptionSelection,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import * as OfferDetailKeys from 'tripPlanner/hooks/reactQueryKeys/offerDetail'
import { OFFER_DETAIL_STALE_TIME } from 'tripPlanner/hooks/api/queryConstants'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import ChatHotelRoomOptionsRoomCard from './ChatHotelRoomOptionsRoomCard'

export type HotelRoomOptionsUIData = Extract<TripChatUiComponentData, { type: 'hotel_room_options' }>

const Container = styled(Sheet)`
  container-type: inline-size;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: ${rem(12)};
  padding: ${rem(16)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const HeaderTitle = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
`

const HeaderAction = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const RoomSelector = styled(CardCarousel)`
  margin: ${rem(12)} 0;
`

const RoomSelectorButton = styled.button`
  width: ${rem(276)};
  flex: 0 0 ${rem(276)};
  min-height: ${rem(66)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(6)} ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;
  text-align: start;
  display: flex;
  align-items: center;
  gap: ${rem(10)};

  &.selected {
    border-color: ${props => props.theme.palette.highlight.primary.normalForeground};
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }
`

const RoomSelectorThumbnail = styled.div`
  width: ${rem(50)};
  height: ${rem(50)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RoomSelectorImage = styled(Image)`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const LoadingRateRow = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(14)};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const EmptyState = styled.div`
  padding: ${rem(16)};
`

const LoadingContent = styled.div`
  padding: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const LoadingImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: ${rem(260)};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.default.six};
`

function getEmptyUnavailableMessage(
  result: HotelRoomOptionsResult,
  intl: IntlShape,
): string {
  switch (result.unavailableReason?.code) {
    case 'minimum_stay_not_met':
      return intl.formatMessage({
        id: 'w2rimA7WFb4vj539xpdG',
        defaultMessage: 'These rooms cannot be priced for the selected stay length.',
        description: 'Safe unavailable message for hotel room options when minimum stay is not met',
      })
    case 'duration_not_offered':
      return intl.formatMessage({
        id: 'OIy2LgoU5oQCbJQaZR9V',
        defaultMessage: 'These rooms cannot be priced for the selected duration.',
        description: 'Safe unavailable message for hotel room options when duration is not offered',
      })
    case 'selection_not_found':
    case 'invalid_selector':
    case 'unsafe_room_rate_selector':
      return intl.formatMessage({
        id: 'T21AhcghoC1YDZJxNiE6',
        defaultMessage: 'These room selections are no longer available.',
        description: 'Safe unavailable message for stale hotel room option selections',
      })
    default:
      return intl.formatMessage({
        id: 'HF52aELZWWJopbJBH1dP',
        defaultMessage: 'These room options are no longer available for the selected stay.',
        description: 'Unavailable message for stale trip planner chat hotel room options',
      })
  }
}

function getPricingUnavailableMessage(
  result: HotelRoomOptionsResult,
  intl: IntlShape,
): string {
  switch (result.pricingUnavailableReason?.code) {
    case 'dates_required':
      return intl.formatMessage({
        id: 'X63b1FzDlnjUZ-BTcGsz',
        defaultMessage: 'Select dates to see current prices for these rooms.',
        description: 'Prompt shown when trip planner chat room options can show room details but not prices',
      })
    default:
      return intl.formatMessage({
        id: 'mY7axbus2ClWpThPisPD',
        defaultMessage: 'Current prices are unavailable for these rooms.',
        description: 'Safe fallback shown when hotel room option pricing is unavailable',
      })
  }
}

function RoomSelectorThumbnailContent({ room }: { room: HotelRoomOptionsResult['roomOptions'][number] }) {
  const thumbnailImageId = room.imageIds[0]

  if (!thumbnailImageId) {
    return <SolidBedIcon size="S" colour="neutral-three" />
  }

  return (
    <RoomSelectorImage
      id={thumbnailImageId}
      width={50}
      height={50}
      fit="center"
      alt=""
    />
  )
}

function RoomOptions({
  result,
  selections,
  itineraryData,
  onSendMessage,
}: {
  result: HotelRoomOptionsResult
  selections: Array<HotelRoomOptionSelection>
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}) {
  const intl = useIntl()
  const pricingUnavailableMessage = getPricingUnavailableMessage(result, intl)
  const [packageCollapseOverrides, setPackageCollapseOverrides] = useState<Record<string, boolean>>({})
  const [multiRoomPackagesCollapsed, setMultiRoomPackagesCollapsed] = useState<boolean | undefined>()
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(result.roomOptions[0]?.roomTypeId)
  const selectedRoom = result.roomOptions.find((room) => room.roomTypeId === selectedRoomTypeId) ?? result.roomOptions[0]
  const showRoomSelector = result.roomOptions.length > 1

  const handleRoomSelectorClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const roomTypeId = event.currentTarget.dataset.roomTypeId
    if (roomTypeId) {
      setSelectedRoomTypeId(roomTypeId)
    }
  }, [])

  const togglePackagesCollapsed = useCallback((roomTypeId: string, nextCollapsed: boolean) => {
    if (showRoomSelector) {
      setMultiRoomPackagesCollapsed(nextCollapsed)
      return
    }

    setPackageCollapseOverrides((current) => ({
      ...current,
      [roomTypeId]: nextCollapsed,
    }))
  }, [showRoomSelector])

  if (!selectedRoom) {
    return null
  }

  return (
    <>
      {showRoomSelector && (
        <RoomSelector
          snap="start"
          gutterStyle="overflow"
          gap={8}
          pageSize="auto"
          gutterSize={28}
          desktopGutterSize={28}
        >
          {result.roomOptions.map((room) => (
            <RoomSelectorButton
              key={room.roomTypeId}
              type="button"
              className={clsx({ selected: room.roomTypeId === selectedRoom.roomTypeId })}
              aria-pressed={room.roomTypeId === selectedRoom.roomTypeId}
              data-room-type-id={room.roomTypeId}
              onClick={handleRoomSelectorClick}
            >
              <RoomSelectorThumbnail>
                <RoomSelectorThumbnailContent room={room} />
              </RoomSelectorThumbnail>
              <BodyText variant="small" weight="bold">
                {room.roomName}
              </BodyText>
            </RoomSelectorButton>
          ))}
        </RoomSelector>
      )}
      <ChatHotelRoomOptionsRoomCard
        key={selectedRoom.roomTypeId}
        room={selectedRoom}
        result={result}
        selections={selections}
        packagesCollapsed={showRoomSelector
          ? multiRoomPackagesCollapsed
          : packageCollapseOverrides[selectedRoom.roomTypeId]}
        pricingUnavailableMessage={pricingUnavailableMessage}
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
        onPackagesCollapsedChange={togglePackagesCollapsed}
      />
    </>
  )
}

interface Props {
  data: HotelRoomOptionsUIData
  onOfferClick?: (offerId: string) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

function ChatHotelRoomOptions({
  data,
  onOfferClick,
  itineraryData,
  onSendMessage,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const queryKey = useMemo(
    () => OfferDetailKeys.roomOptions(
      data.offerId,
      geo.currentRegionCode,
      data.checkIn,
      data.checkOut,
      data.nights,
      data.rooms,
      data.selections,
    ),
    [data.checkIn, data.checkOut, data.nights, data.offerId, data.rooms, data.selections, geo.currentRegionCode],
  )
  const query = useQuery({
    queryKey,
    queryFn: () => getHotelRoomOptions(data.offerId, {
      region: geo.currentRegionCode,
      ...(data.checkIn ? { checkIn: data.checkIn } : {}),
      ...(data.checkOut ? { checkOut: data.checkOut } : {}),
      ...(data.nights === undefined ? {} : { nights: data.nights }),
      ...(data.rooms ? { rooms: data.rooms } : {}),
      selections: data.selections,
    }),
    staleTime: OFFER_DETAIL_STALE_TIME,
    retry: 1,
  })

  const result = query.data
  const hasRoomOptions = (result?.roomOptions.length ?? 0) > 0
  const handleOfferClick = useCallback(() => {
    onOfferClick?.(data.offerId)
  }, [data.offerId, onOfferClick])

  return (
    <Container border="neutral-five" borderRadius="L" background="neutral-eight">
      <Header>
        <HeaderTitle>
          <Heading variant="heading5">
            {result?.hotelName ?? intl.formatMessage({
              id: '_pABERd3t-fScz-qaAXG',
              defaultMessage: 'Room options',
              description: 'Fallback heading for live hotel room options in trip planner chat',
            })}
          </Heading>
        </HeaderTitle>
        {result && (
          <HeaderAction>
            <TextButton
              kind="secondary"
              size="small"
              onClick={handleOfferClick}
              href={onOfferClick ? undefined : result.deeplinkUrl}
            >
              {intl.formatMessage({
                id: '_y-tLaCoJS6VYGNUlLIO',
                defaultMessage: 'View offer',
                description: 'Button label for opening a hotel offer from trip planner chat room options',
              })}
            </TextButton>
          </HeaderAction>
        )}
      </Header>

      {query.isLoading && (
        <LoadingContent
          aria-label={intl.formatMessage({
            id: 'aPgTN4SUkrKt0f6G45zF',
            defaultMessage: 'Checking live room options...',
            description: 'Loading message for trip planner chat hotel room options',
          })}
        >
          <TextLoadingBox typography="heading6" width="55%" />
          <LoadingImage />
          <LoadingRateRow>
            <TextLoadingBox typography="body-medium" width="65%" />
            <TextLoadingBox typography="body-small" width="45%" />
            <TextLoadingBox typography="body-small" width="80%" />
          </LoadingRateRow>
        </LoadingContent>
      )}
      {query.isError && (
        <EmptyState>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'antsNkS9Q7JJScJu1qRA',
              defaultMessage: 'These room options could not be loaded right now.',
              description: 'Error message for trip planner chat hotel room options',
            })}
          </BodyText>
        </EmptyState>
      )}
      {result && !hasRoomOptions && !result.available && (
        <EmptyState>
          <BodyText variant="medium">
            {getEmptyUnavailableMessage(result, intl)}
          </BodyText>
        </EmptyState>
      )}
      {result && !hasRoomOptions && result.available && (
        <EmptyState>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'TSEpaafKmaJlMto_THD3',
              defaultMessage: 'No room options are available to display right now.',
              description: 'Defensive empty state when hotel room options response is available but contains no room rows',
            })}
          </BodyText>
        </EmptyState>
      )}
      {result && hasRoomOptions && (
        <RoomOptions
          result={result}
          selections={data.selections}
          itineraryData={itineraryData}
          onSendMessage={onSendMessage}
        />
      )}
    </Container>
  )
}

export default memo(ChatHotelRoomOptions)
